import { Stack, Typography } from "@mui/material";
import {
  createRootRouteWithContext,
  HeadContent,
  Outlet,
} from "@tanstack/react-router";
import { TanStackRouterDevtools } from "@tanstack/react-router-devtools";

import type { AuthContextType } from "@/contexts/AuthContext";
import type { QueryClient } from "@tanstack/react-query";

import { ErrorComponent } from "@/components/ErrorComponent";

interface RootRouteContext {
  auth: AuthContextType;
  queryClient: QueryClient;
}

/**
 * The app's 404. Exported so the router can also use it as
 * `defaultNotFoundComponent`: a `notFound()` thrown from a route's beforeLoad
 * does not reach this boundary on its own, and without a default it renders a
 * blank page instead of anything at all.
 */
export function NotFoundComponent() {
  return (
    <Stack
      sx={{
        height: "100%",
        alignItems: "center",
        justifyContent: "center",
        padding: 10,
        textAlign: "center",
      }}
    >
      <Typography variant="h2">
        This page only exists
        <Typography variant="h1" component="span" sx={{ display: "block" }}>
          IN YOUR MIND!
        </Typography>
      </Typography>
    </Stack>
  );
}

export function RootComponent() {
  return (
    <>
      <HeadContent />
      <Outlet />
      {import.meta.env.VITE_ENABLE_DEVTOOLS !== "false" && (
        <TanStackRouterDevtools />
      )}
    </>
  );
}

export const Route = createRootRouteWithContext<RootRouteContext>()({
  head: () => ({
    meta: [
      {
        title: "50/50 Club | Queen One",
      },
    ],
    links: [
      {
        rel: "icon",
        href: "/favicon.svg",
      },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      {
        rel: "preconnect",
        href: "https://fonts.gstatic.com",
        crossOrigin: "anonymous",
      },
      {
        rel: "preconnect",
        href: "https://cdn.queen.one",
        crossOrigin: "anonymous",
      },
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap",
      },
      {
        rel: "stylesheet",
        href: "https://api.fontshare.com/v2/css?f[]=cabinet-grotesk@400,500&display=swap",
      },
      {
        rel: "stylesheet",
        href: "https://cdn.queen.one/connect/ui/fonts/lust/lust.css",
      },
      {
        rel: "stylesheet",
        href: "https://cdn.queen.one/connect/ui/fonts/geas/geas.css",
      },
      {
        rel: "preload",
        as: "image",
        href: "/empty-hex-bg.png",
      },
    ],
  }),
  component: RootComponent,
  errorComponent: ErrorComponent,
  notFoundComponent: NotFoundComponent,
});
